/** Controls — buttons, pills, chips, tabs, search. */
import { useState } from 'react';
import {
  Flame,
  Clock,
  Target,
  Zap,
  ArrowRight,
  Play,
  Search,
  Trash2,
  Download,
  Upload,
  Plus,
  Settings,
  ExternalLink,
  Award,
  TrendingUp,
  MoreHorizontal,
  Pencil,
  Palette,
} from 'lucide-react';
import { useHover, TRACK, eyebrow, meta, body, Section } from '../shared';
import {
  Tabs,
  Button,
  TextField,
  SearchField,
  Menu,
  MenuItem,
  MenuTrigger,
  MenuSeparator,
  Badge,
  Checkbox,
  FileDropZone,
  Tag,
  TagButton,
  Select,
  Slider,
  ColorPalettePicker,
} from '@/interfaces/learner/ui';
import { Button as AriaButton } from 'react-aria-components';

// ─── Stat pill ────────────────────────────────────────────────────────────────

function StatPill({
  icon: Icon,
  value,
  label,
  accent,
}: {
  icon: React.ComponentType<{ size?: number; strokeWidth?: number; style?: React.CSSProperties }>;
  value: string;
  label: string;
  accent?: string;
}) {
  const [h, handlers] = useHover();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '5px 13px 5px 9px',
        background: 'transparent',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 9999,
        cursor: 'default',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        borderColor: h ? 'var(--shell-command-border-hover)' : 'var(--shell-command-border)',
        boxShadow: h ? '0 1px 4px rgba(0,0,0,0.06)' : '0 1px 2px rgba(0,0,0,0.03)',
      }}
      {...handlers}
    >
      <Icon size={14} strokeWidth={2.2} style={{ color: accent ?? 'var(--color-text-muted)' }} />
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-text)',
          letterSpacing: '-0.01em',
        }}
      >
        {value}
      </span>
      <span style={meta}>{label}</span>
    </div>
  );
}

// ─── Search bar ───────────────────────────────────────────────────────────────

function SearchBar() {
  const [h, handlers] = useHover();
  return (
    <button
      type="button"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        maxWidth: 560,
        padding: '11px 18px',
        background: 'var(--color-surface)',
        border: '1px solid var(--shell-command-border)',
        borderRadius: 9999,
        backdropFilter: 'blur(12px) saturate(1.2)',
        WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
        boxShadow: h
          ? 'inset 0 0.5px 0 var(--color-surface-highlight), 0 1px 4px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.03)'
          : 'inset 0 0.5px 0 var(--color-surface-highlight), 0 1px 2px rgba(0,0,0,0.03)',
        cursor: 'text',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        borderColor: h ? 'var(--shell-command-border-hover)' : 'var(--shell-command-border)',
      }}
      {...handlers}
    >
      <Search size={15} strokeWidth={2} style={{ color: 'var(--color-text-muted)' }} />
      <span style={{ ...body, fontSize: 14, fontWeight: 400 }}>
        Search courses, topics, or skills...
      </span>
      <kbd
        className="ml-auto hidden min-[640px]:inline"
        style={{
          ...meta,
          fontSize: 12,
          fontWeight: 500,
          fontFamily: 'inherit',
          background: 'rgba(0,0,0,0.04)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 5,
          padding: '2px 6px',
          lineHeight: 1,
        }}
      >
        /
      </kbd>
    </button>
  );
}

// ─── Category Pill Row ────────────────────────────────────────────────────────

function CategoryPills() {
  const [active, setActive] = useState('All');
  const cats = [
    'All',
    'Computer Science',
    'Mathematics',
    'Chemistry',
    'Physics',
    'Economics',
    'Biology',
    'History',
  ];

  return (
    <div
      className="flex gap-2 overflow-x-auto scrollbar-none"
      style={{ margin: '0 -6px', padding: '0 6px' }}
    >
      {cats.map((c) => {
        const isActive = c === active;
        return (
          <button
            key={c}
            type="button"
            onClick={() => setActive(c)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '7px 16px',
              borderRadius: 9999,
              fontSize: 13,
              fontWeight: isActive ? 600 : 400,
              cursor: 'pointer',
              transition: 'all 0.15s',
              background: isActive ? 'var(--color-accent)' : 'var(--color-surface)',
              color: isActive ? '#fff' : 'var(--color-text-secondary)',
              border: isActive
                ? '1px solid var(--color-accent)'
                : '1px solid var(--color-surface-border)',
              backdropFilter: isActive ? undefined : 'blur(12px)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {c}
          </button>
        );
      })}
    </div>
  );
}

// ─── Streak Dropdown Panel ────────────────────────────────────────────────────

function StreakDropdown() {
  const streak = 12;
  const longest = 21;
  const days = [
    { label: 'M', done: true },
    { label: 'T', done: true },
    { label: 'W', done: true },
    { label: 'T', done: true },
    { label: 'F', done: false, today: true },
    { label: 'S', done: false },
    { label: 'S', done: false },
  ];

  const milestones = [
    { target: 7, label: '1 week', reached: true },
    { target: 14, label: '2 weeks', reached: false },
    { target: 30, label: '1 month', reached: false },
  ];

  return (
    <div
      style={{
        width: 320,
        borderRadius: 16,
        background: 'var(--color-surface)',
        backdropFilter: 'blur(24px) saturate(1.4)',
        WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
        border: '1px solid var(--color-surface-border)',
        boxShadow:
          'inset 0 0.5px 0 var(--color-surface-highlight), 0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}
    >
      {/* Header with gradient */}
      <div
        style={{
          padding: '20px 20px 16px',
          background:
            'linear-gradient(135deg, rgba(255,155,74,0.06), rgba(255,110,177,0.08), rgba(138,108,240,0.04))',
        }}
      >
        <div className="flex items-center gap-4">
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: 'linear-gradient(135deg, #ff9b4a, #ff6eb1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(255,110,177,0.25)',
            }}
          >
            <Flame size={24} strokeWidth={2} style={{ color: '#fff' }} />
          </div>
          <div>
            <div className="flex items-baseline gap-2">
              <span
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: 'var(--color-text)',
                  letterSpacing: '-0.03em',
                  lineHeight: 1,
                }}
              >
                {streak}
              </span>
              <span style={{ ...meta, fontWeight: 500, color: 'var(--color-text-secondary)' }}>
                day streak
              </span>
            </div>
            <p style={{ ...meta, marginTop: 3 }}>Longest: {longest} days</p>
          </div>
        </div>
      </div>

      {/* Week dots */}
      <div style={{ padding: '14px 20px 16px' }}>
        <p style={{ ...eyebrow, fontSize: 12, marginBottom: 10 }}>This week</p>
        <div className="flex gap-1.5">
          {days.map((d, i) => (
            <div key={i} className="flex flex-1 flex-col items-center gap-1.5">
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: d.done
                    ? 'linear-gradient(135deg, #ff9b4a, #ff6eb1)'
                    : d.today
                      ? 'color-mix(in srgb, var(--color-accent) 8%, transparent)'
                      : TRACK,
                  border:
                    d.today && !d.done
                      ? '1.5px dashed color-mix(in srgb, var(--color-accent) 30%, transparent)'
                      : '1.5px solid transparent',
                }}
              >
                {d.done ? (
                  <Flame size={14} strokeWidth={2.2} style={{ color: '#fff' }} />
                ) : (
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      color: d.today ? 'var(--color-accent)' : 'var(--color-text-muted)',
                      opacity: d.today ? 1 : 0.4,
                    }}
                  >
                    {d.today ? '?' : ''}
                  </span>
                )}
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: d.today ? 600 : 400,
                  color: d.today
                    ? 'var(--color-accent)'
                    : d.done
                      ? 'var(--color-text-secondary)'
                      : 'var(--color-text-muted)',
                }}
              >
                {d.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: 'var(--color-surface-border)', margin: '0 20px' }} />

      {/* Milestones */}
      <div style={{ padding: '14px 20px 18px' }}>
        <p style={{ ...eyebrow, fontSize: 12, marginBottom: 10 }}>Milestones</p>
        <div className="flex flex-col gap-2.5">
          {milestones.map((m) => (
            <div key={m.target} className="flex items-center gap-3">
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 7,
                  background: m.reached ? 'linear-gradient(135deg, #ff9b4a, #ff6eb1)' : TRACK,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {m.reached ? (
                  <Award size={12} strokeWidth={2.5} style={{ color: '#fff' }} />
                ) : (
                  <Award
                    size={12}
                    strokeWidth={2}
                    style={{ color: 'var(--color-text-muted)', opacity: 0.4 }}
                  />
                )}
              </div>
              <span
                style={{
                  ...meta,
                  flex: 1,
                  color: m.reached ? 'var(--color-text)' : 'var(--color-text-muted)',
                  fontWeight: m.reached ? 500 : 400,
                }}
              >
                {m.label}
              </span>
              <span
                style={{
                  ...meta,
                  fontSize: 12,
                  fontWeight: 600,
                  color: m.reached ? 'var(--color-success-text)' : 'var(--color-text-muted)',
                }}
              >
                {m.reached ? `${m.target} days ✓` : `${m.target - streak} to go`}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer CTA */}
      <div
        style={{
          padding: '12px 20px 14px',
          background: 'rgba(0,0,0,0.015)',
          borderTop: '1px solid var(--color-surface-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-accent-text)',
        }}
      >
        <TrendingUp size={14} strokeWidth={2} />
        View full insights
      </div>
    </div>
  );
}

// ─── Color Palette Picker Demo ────────────────────────────────────────────────

function ColorPalettePickerDemo() {
  const [color, setColor] = useState('#8b5cf6');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <ColorPalettePicker value={color} onChange={setColor} />
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
        Selected: <span style={{ color, fontWeight: 600 }}>{color}</span>
      </p>
    </div>
  );
}

// ─── Tab content ──────────────────────────────────────────────────────────────

export default function ControlsTab() {
  const [includeImages, setIncludeImages] = useState(true);
  const [selectedModule, setSelectedModule] = useState('respiratory');
  const [questionCount, setQuestionCount] = useState(12);
  const [demoFile, setDemoFile] = useState<File | null>(null);

  return (
    <>
      <Section label="Buttons — Variants">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" icon={<Play size={15} strokeWidth={2} />}>
            Start lesson
          </Button>
          <Button variant="secondary" icon={<ArrowRight size={15} strokeWidth={2} />}>
            Continue
          </Button>
          <Button variant="gradient" icon={<Zap size={15} strokeWidth={2} />}>
            Generate
          </Button>
          <Button variant="ghost">Cancel</Button>
          <Button variant="danger" icon={<Trash2 size={15} strokeWidth={2} />}>
            Delete
          </Button>
        </div>
      </Section>

      <Section label="Buttons — Sizes">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" size="sm">
            Small
          </Button>
          <Button variant="primary" size="md">
            Medium
          </Button>
          <Button variant="primary" size="lg">
            Large
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button variant="secondary" size="sm" icon={<Plus size={13} strokeWidth={2} />}>
            Add
          </Button>
          <Button variant="secondary" size="md" icon={<Settings size={15} strokeWidth={2} />}>
            Settings
          </Button>
          <Button variant="secondary" size="lg" icon={<Download size={16} strokeWidth={2} />}>
            Download
          </Button>
        </div>
      </Section>

      <Section label="Buttons — States">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" isDisabled>
            Disabled
          </Button>
          <Button variant="secondary" isDisabled>
            Disabled
          </Button>
          <Button variant="gradient" isDisabled>
            Disabled
          </Button>
          <Button variant="primary" loading>
            Saving...
          </Button>
          <Button variant="secondary" loading>
            Loading
          </Button>
        </div>
      </Section>

      <Section label="Buttons — With Icons">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="primary" icon={<Play size={15} strokeWidth={2.5} />}>
            Resume lesson
          </Button>
          <Button
            variant="gradient"
            icon={<Zap size={15} strokeWidth={2} />}
            iconRight={<ExternalLink size={13} strokeWidth={2} />}
          >
            Generate &amp; export
          </Button>
          <Button variant="ghost" icon={<ArrowRight size={14} strokeWidth={2} />}>
            Skip
          </Button>
        </div>
      </Section>

      <Section label="Stat Pills">
        <div className="flex flex-wrap gap-3">
          <StatPill icon={Flame} value="12" label="day streak" accent="var(--color-warning)" />
          <StatPill icon={Clock} value="4.2h" label="this week" accent="var(--color-info)" />
          <StatPill icon={Target} value="87%" label="accuracy" accent="var(--color-success)" />
          <StatPill icon={Zap} value="23" label="lessons" accent="var(--color-accent)" />
        </div>
      </Section>

      <Section label="Tags">
        <div className="flex flex-wrap gap-2">
          <Tag tone="accent">Cardiology</Tag>
          <Tag>Practice</Tag>
          <Tag tone="success">Mastered</Tag>
          <Tag tone="warning">Needs review</Tag>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <TagButton size="sm" tone="accent" variant="solid">
            All topics
          </TagButton>
          <TagButton size="sm" variant="outline">
            Mathematics
          </TagButton>
          <TagButton size="sm" variant="outline">
            Physics
          </TagButton>
          <TagButton size="sm" variant="outline">
            Chemistry
          </TagButton>
          <TagButton size="sm" variant="outline">
            Biology
          </TagButton>
          <TagButton size="sm" variant="outline">
            Computer Science
          </TagButton>
        </div>
      </Section>

      <Section label="Checkboxes">
        <div className="flex flex-wrap items-center gap-5">
          <Checkbox isSelected={includeImages} onChange={setIncludeImages}>
            Include image questions
          </Checkbox>
          <Checkbox isIndeterminate>Some lessons selected</Checkbox>
          <Checkbox isDisabled>Unavailable option</Checkbox>
        </div>
      </Section>

      <Section label="Tabs — Selector Only">
        <Tabs
          tabs={[
            { id: 'overview', label: 'Overview' },
            { id: 'progress', label: 'Progress' },
            { id: 'activity', label: 'Activity' },
            { id: 'settings', label: 'Settings' },
          ]}
        />
      </Section>

      <Section label="Tabs — With Panels">
        <Tabs
          tabs={[
            {
              id: 'all',
              label: 'All courses',
              content: (
                <p style={{ ...body, marginTop: 4 }}>
                  Showing all 24 courses across every category.
                </p>
              ),
            },
            {
              id: 'active',
              label: 'In progress',
              content: (
                <p style={{ ...body, marginTop: 4 }}>
                  4 courses you&apos;re currently working through.
                </p>
              ),
            },
            {
              id: 'completed',
              label: 'Completed',
              content: <p style={{ ...body, marginTop: 4 }}>12 courses finished — nice work.</p>,
            },
            {
              id: 'saved',
              label: 'Saved',
              content: <p style={{ ...body, marginTop: 4 }}>3 courses bookmarked for later.</p>,
            },
            { id: 'disabled', label: 'Archived', isDisabled: true },
          ]}
        />
      </Section>

      <Section label="Text Fields — Sizes">
        <div className="flex flex-col gap-4" style={{ maxWidth: 400 }}>
          <TextField size="sm" placeholder="Small input" />
          <TextField size="md" placeholder="Medium input" />
          <TextField size="lg" placeholder="Large input" />
        </div>
      </Section>

      <Section label="Text Fields — With Labels">
        <div className="flex flex-col gap-5" style={{ maxWidth: 400 }}>
          <TextField
            label="Full name"
            placeholder="Enter your name"
            description="As it appears on your profile."
          />
          <TextField
            label="Email"
            placeholder="you@example.com"
            icon={<Settings size={15} strokeWidth={2} />}
          />
          <TextField
            label="Invite code"
            placeholder="ABC-123"
            isInvalid
            errorMessage="This code has expired. Please request a new one."
          />
        </div>
      </Section>

      <Section label="Text Fields — Textarea">
        <div style={{ maxWidth: 500 }}>
          <TextField
            label="Feedback"
            placeholder="Tell us what you think..."
            multiline
            rows={4}
            description="Optional — helps us improve the experience."
          />
        </div>
      </Section>

      <Section label="Text Fields — Disabled">
        <div style={{ maxWidth: 400 }}>
          <TextField label="Organisation" value="Formency" isDisabled />
        </div>
      </Section>

      <Section label="Search Field">
        <div className="flex flex-col gap-4" style={{ maxWidth: 400 }}>
          <SearchField placeholder="Search courses, topics, or skills..." />
          <SearchField size="sm" placeholder="Filter lessons..." />
        </div>
      </Section>

      <Section label="Select">
        <div style={{ maxWidth: 400 }}>
          <Select
            label="Module"
            options={[
              { value: 'respiratory', label: 'Respiratory Medicine' },
              { value: 'cardiology', label: 'Cardiology' },
              { value: 'neurology', label: 'Neurology' },
            ]}
            selectedKey={selectedModule}
            onSelectionChange={(key) => setSelectedModule(String(key))}
          />
        </div>
      </Section>

      <Section label="Slider">
        <div style={{ maxWidth: 420 }}>
          <Slider
            label="Number of Questions"
            minValue={1}
            maxValue={30}
            step={1}
            value={questionCount}
            output={questionCount}
            onChange={setQuestionCount}
            description="Used in the revision session creator."
          />
        </div>
      </Section>

      <Section label="File Upload">
        <div style={{ maxWidth: 420 }}>
          <FileDropZone
            acceptedFileTypes={['.pdf', '.docx', '.txt']}
            aria-label="Upload a reference file"
            description="Lecture notes, textbooks, syllabi, or study guides"
            icon={<Upload size={20} strokeWidth={2} />}
            label="Drop a file"
            meta=".pdf .docx .txt · max 2 MB"
            onFileSelect={setDemoFile}
          />
          {demoFile && (
            <p className="mt-2" style={{ ...meta }}>
              Selected: {demoFile.name}
            </p>
          )}
        </div>
      </Section>

      <Section label="Search — Legacy Mockup">
        <SearchBar />
      </Section>

      <Section label="Category Filters">
        <CategoryPills />
      </Section>

      <Section label="Streak Dropdown (from shell header)">
        <StreakDropdown />
      </Section>

      <Section label="Menu">
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <MenuTrigger>
            <AriaButton
              className="cursor-pointer"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                borderRadius: 10,
                background: 'var(--color-surface-muted)',
                border: 'none',
                color: 'var(--color-text)',
                fontSize: 14,
                fontWeight: 500,
                fontFamily: 'var(--font-sans)',
              }}
            >
              <MoreHorizontal size={16} strokeWidth={1.8} />
              Options
            </AriaButton>
            <Menu onAction={() => {}}>
              <MenuItem id="rename" icon={<Pencil size={14} strokeWidth={1.8} />}>
                Rename
              </MenuItem>
              <MenuItem id="color" icon={<Palette size={14} strokeWidth={1.8} />}>
                Change color
              </MenuItem>
              <MenuSeparator />
              <MenuItem id="delete" variant="danger" icon={<Trash2 size={14} strokeWidth={1.8} />}>
                Delete
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </div>
      </Section>

      <Section label="Badge">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Badge>Default</Badge>
          <Badge variant="accent">Accent</Badge>
          <Badge variant="success">Success</Badge>
          <Badge variant="warning">Warning</Badge>
          <Badge variant="error">Error</Badge>
        </div>
      </Section>

      <Section label="Color Palette Picker">
        <ColorPalettePickerDemo />
      </Section>
    </>
  );
}
